import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoIosArrowForward } from "react-icons/io";
import { imgUrl } from '../utils/imgUrl';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { place_order, checkout_start, checkout_cancel, checkout_renew, messageClear } from '../store/reducers/orderReducer';
import { get_store_config } from '../store/reducers/homeReducer';

// Contador de la reserva del checkout. Solo aparece aquí: en el carrito no
// hay reserva ni presión (ECO-06).
const ReservaBanner = ({ checkout, checkoutError, loading, onRenew, onExpired }) => {
    const [ahora, setAhora] = useState(Date.now())
    useEffect(() => { const t = setInterval(() => setAhora(Date.now()), 1000); return () => clearInterval(t) }, [])
    const seg = checkout?.expires_at ? Math.max(0, Math.floor((new Date(checkout.expires_at + (checkout.expires_at.endsWith('Z') ? '' : 'Z')).getTime() - ahora) / 1000)) : null
    const [avisado, setAvisado] = useState(false)
    useEffect(() => {
        if (checkout?.active && seg === 0 && !avisado) { setAvisado(true); onExpired() }
        if (seg > 0 && avisado) setAvisado(false)
    }, [seg, checkout, avisado, onExpired])

    if (loading) return <div className='bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-600'>Verificando la disponibilidad de tus productos…</div>
    if (checkoutError) {
        return (
            <div className='bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700 flex flex-col gap-2' role='alert'>
                <span>{checkoutError.message}</span>
                <div className='flex gap-3'>
                    <button onClick={onRenew} className='underline'>Volver a verificar</button>
                    <Link to='/card' className='underline'>Ir al carrito</Link>
                </div>
            </div>
        )
    }
    if (!checkout) return null
    if (checkout.active && seg > 0) {
        const m = String(Math.floor(seg / 60)).padStart(2, '0'), ss = String(seg % 60).padStart(2, '0')
        return (
            <div className='bg-cyan-50 border border-cyan-200 rounded-md p-3 text-sm text-cyan-900 flex items-center justify-between gap-3' role='status' aria-live='polite'>
                <span>Tus productos están reservados por <strong className='font-mono'>{m}:{ss}</strong> mientras completás tu pedido.</span>
                {checkout.pendientes_proveedor?.length > 0 && (
                    <span className='text-xs text-amber-700'>{checkout.pendientes_proveedor.length} bajo pedido</span>
                )}
            </div>
        )
    }
    if (checkout.active === false && checkout.pendientes_proveedor?.length > 0 && !checkout.reservations?.length) {
        return (
            <div className='bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800' role='status'>
                Tus productos son bajo pedido: se piden al proveedor y te avisamos la fecha de entrega al confirmar.
            </div>
        )
    }
    return (
        <div className='bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 flex flex-col gap-2' role='alert'>
            <span>Tu reserva expiró. Estamos verificando nuevamente la disponibilidad de tus productos.</span>
            <button onClick={onRenew} className='underline self-start'>Verificar ahora</button>
        </div>
    )
}

// Cómo paga el cliente. Los métodos salen de `/store/config`: el admin decide
// qué acepta la plataforma y acá no se inventa ninguno. Hasta este cambio la
// pantalla ofrecía contra entrega fijo y el pedido se guardaba sin método, así
// que no se sabía si ese dinero estaba cobrado o andaba en la calle.
const BloquePago = ({ metodos, elegido, onElegir, referencia, onReferencia, total, topeCod,
                      porCobrar, departamento }) => {
    if (metodos === null) {
        return <div className='bg-slate-50 border border-slate-200 rounded-md p-3 text-sm text-slate-600'>
            Cargando las formas de pago…
        </div>
    }
    if (metodos.length === 0) {
        return <div className='bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700' role='alert'>
            La tienda no tiene ninguna forma de pago habilitada. Escribinos para completar tu pedido.
        </div>
    }
    // El tope del contra entrega no es un capricho: mandar a cobrar un monto
    // grande con un mensajero es un riesgo que el negocio decide no tomar.
    const sobreTope = (m) => m === 'contra_entrega' && topeCod != null && total > topeCod
    // Al interior la mensajería cobra **su** envío, no nuestra venta, así que no
    // hay contra entrega. Se deshabilita y se dice por qué: dejar marcarlo para
    // deseleccionarlo solo se ve como una pantalla que se pelea con la persona.
    const sinCod = (m) => m === 'contra_entrega' && porCobrar
    const motivo = (m) => sinCod(m)
        ? `A ${departamento} el envío lo cobra la mensajería al entregarte, así que la compra se paga por adelantado.`
        : sobreTope(m) ? `Solo para pedidos de hasta Q${topeCod}. El tuyo es de Q${total}.`
        : null

    return (
        <div className='flex flex-col gap-2'>
            <h3 className='font-bold'>¿Cómo querés pagar?</h3>
            {metodos.map(m => {
                const bloqueado = sobreTope(m.valor) || sinCod(m.valor)
                const activo = elegido === m.valor
                return (
                    <label key={m.valor}
                           className={`flex items-start gap-2 p-3 rounded-md border text-sm ${
                               bloqueado ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                                         : activo ? 'border-[#059473] bg-[#059473]/5 cursor-pointer'
                                                  : 'border-slate-200 hover:border-[#059473] cursor-pointer'}`}>
                        <input type='radio' name='metodo_pago' value={m.valor} className='mt-1 accent-[#059473]'
                               checked={activo} disabled={bloqueado}
                               onChange={() => onElegir(m.valor)} />
                        <span className='min-w-0'>
                            <span className='block font-medium text-slate-700'>{m.etiqueta}</span>
                            <span className='block text-xs text-slate-500'>
                                {motivo(m.valor) || m.ayuda}
                            </span>
                        </span>
                    </label>
                )
            })}
            {elegido === 'transferencia' && (
                <div className='flex flex-col gap-1'>
                    <label htmlFor='referencia-pago' className='text-xs font-semibold uppercase tracking-wide text-slate-500'>
                        Número de la transferencia
                    </label>
                    <input id='referencia-pago' value={referencia} onChange={e => onReferencia(e.target.value)}
                           className='outline-none px-3 py-2 border rounded-md text-slate-700'
                           placeholder='Ej. 884512' />
                    <span className='text-xs text-slate-500'>
                        Es con lo que confirmamos tu pago en el banco. Preparamos tu pedido en cuanto lo verifiquemos.
                    </span>
                </div>
            )}
        </div>
    )
}

// La misma regla que `politica_envio` del servidor, para poder mostrar el total
// antes de colocar el pedido. **El servidor la vuelve a aplicar y es el que
// cobra**: esto es para que la pantalla no mienta, no para decidir.
const politicaEnvio = (envio, departamento) => {
    if (!departamento) return { flete: null, loPaga: null }
    const locales = (envio?.departamentos_locales || []).map(d => d.trim().toLowerCase())
    if (locales.includes(departamento.trim().toLowerCase())) {
        return { flete: Number(envio?.flete_local || 0), loPaga: 'NOSOTROS' }
    }
    return { flete: 0, loPaga: 'CLIENTE_AL_RECIBIR' }
}

const Shipping = () => {

    // `shipping_fee` ya no viene del carrito: el carrito no sabe a dónde va el
    // pedido. El flete lo decide el departamento (`fleteEfectivo`) y el
    // servidor lo vuelve a calcular al cobrarlo.
    const { state: {products,price,items }} = useLocation()
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const {userInfo} = useSelector(state => state.auth) 
    const { checkout, checkoutError, checkoutLoading, errorMessage } = useSelector(state => state.order)
    const { metodosPago, codMontoMaximo, departamentos, municipios, envio } = useSelector(state => state.home)

    const [metodo, setMetodo] = useState('')
    const [referencia, setReferencia] = useState('')
    useEffect(() => { dispatch(get_store_config()) }, [dispatch])
    // Si queda un solo método habilitado, se preselecciona: no tiene sentido
    // hacer elegir entre una sola opción.
    useEffect(() => {
        if (metodosPago?.length === 1) setMetodo(metodosPago[0].valor)
    }, [metodosPago])


    // Al entrar al checkout se reserva; al salir sin confirmar se libera.
    const [confirmado, setConfirmado] = useState(false)
    useEffect(() => {
        dispatch(checkout_start())
        return () => { if (!confirmado) dispatch(checkout_cancel()) }
    }, [])   // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (errorMessage) { toast.error(errorMessage); dispatch(messageClear()) }
    }, [errorMessage, dispatch])
    const renovar = () => dispatch(checkout_renew())
    const reservaVigente = !!checkout?.active || (checkout?.active === false && checkout?.pendientes_proveedor?.length > 0)
    const lineas = (products || []).flatMap(p => Array.isArray(p.products)
        ? p.products.map(pt => ({ name: pt.productInfo?.name, brand: pt.productInfo?.brand, image: pt.productInfo?.images?.[0] || pt.productInfo?.image,
                                   price: pt.productInfo?.price, quantity: pt.quantity, bajo_pedido: pt.productInfo?.bajo_pedido, dias_entrega: pt.productInfo?.dias_entrega }))
        : [p])

    const [res, setRes] = useState(false)
    // Las claves son las que el API espera (`ShippingInfo`). Antes la pantalla
    // mandaba `province`/`city` y Pydantic las descartaba sin avisar: el
    // departamento **nunca llegaba**, así que ni el rastreo mostraba el destino
    // ni se podía decidir cómo se envía.
    const [state, setState] = useState({
        name: '',
        address: '',
        phone: '',
        postCode: '',
        department: '',
        municipality: '',
        zona: ''
    })

    // Va después de `state`: calcularlo arriba lo leía antes de declararlo y la
    // página se caía en blanco (TDZ de `const`), que es como se rompieron
    // cuatro recorridos de golpe.
    const pol = politicaEnvio(envio, state.department)
    const fleteEfectivo = pol.flete ?? 0
    const porCobrar = pol.loPaga === 'CLIENTE_AL_RECIBIR'

    // Al interior la mensajería cobra su envío, no nuestra venta: no hay contra
    // entrega. Si estaba elegido y se cambia de departamento, se deselecciona.
    // Va acá y no arriba porque el array de dependencias se evalúa en el
    // render, así que leer `porCobrar` antes de declararlo tira la página.
    useEffect(() => {
        if (porCobrar && metodo === 'contra_entrega') setMetodo('')
    }, [porCobrar, metodo])

    const inputHandle = (e) => {
        setState({
            ...state,
            [e.target.name]: e.target.value,
            // Cambiar de departamento deja sin sentido el municipio elegido:
            // pertenecía al anterior.
            ...(e.target.name === 'department' ? { municipality: '' } : {})
        })
    }

    const save = (e) => {
        e.preventDefault()
        const { name, address, phone, department, municipality } = state
        if (!department) { toast.error('Elegí tu departamento: de eso depende cómo se envía'); return }
        if (name && address && phone && municipality) { setRes(true); return }
        toast.error('Faltan datos de la entrega: nombre, dirección, teléfono y municipio')
    }

    const placeOrder = () => {
        if (!reservaVigente) { toast.error('Tu reserva expiró: verificá la disponibilidad antes de confirmar'); return }
        if (!metodo) { toast.error('Elegí cómo querés pagar tu pedido'); return }
        if (metodo === 'transferencia' && !referencia.trim()) {
            toast.error('Escribí el número de tu transferencia para que podamos confirmarla'); return
        }
        setConfirmado(true)
        dispatch(place_order({
            price,
            products,
            shipping_fee: fleteEfectivo,
            items,
            shippingInfo : state,
            userId: userInfo.id,
            payment_method: metodo,
            payment_ref: referencia.trim() || null,
            navigate

        }))
    }

    return (
        <div>
          <Header/>
          <section className='h-[220px] mt-6 bg-cover bg-no-repeat relative bg-left' style={{ backgroundImage: 'url(/images/banner/shop.png)' }}>
            <div className='absolute left-0 top-0 w-full h-full bg-[#2422228a]'>
                <div className='w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto'>
                    <div className='flex flex-col justify-center gap-1 items-center h-full w-full text-white'>
                <h2 className='text-3xl font-bold'>Shipping Page </h2>
                <div className='flex justify-center items-center gap-2 text-2xl w-full'>
                        <Link to='/'>Home</Link>
                        <span className='pt-1'>
                        <IoIosArrowForward />
                        </span>
                        <span>Shipping </span>
                      </div>
                    </div> 
                </div> 
            </div> 
           </section>


    <section className='bg-[#eeeeee]'>
        <div className='w-[85%] lg:w-[90%] md:w-[90%] sm:w-[90%] mx-auto py-16'>
           <div className='w-full flex flex-wrap'>
            <div className='w-[67%] md-lg:w-full'>
                <div className='flex flex-col gap-3'>
                    <div className='bg-white p-6 shadow-sm rounded-md'>

                        <h2 className='text-slate-600 font-bold pb-3'>Shipping Information </h2>

            {
              !res && <>
             <form onSubmit={save}>
            <div className='flex md:flex-col md:gap-2 w-full gap-5 text-slate-600'>
            <div className='flex flex-col gap-1 mb-2 w-full'>
                <label htmlFor="name"> Nombre </label>
                <input onChange={inputHandle} value={state.name} type="text" className='w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md' name="name" id="name" placeholder='Nombre de quien recibe' /> 
            </div>

            <div className='flex flex-col gap-1 mb-2 w-full'>
                <label htmlFor="address"> Dirección </label>
                <input onChange={inputHandle} value={state.address} type="text" className='w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md' name="address" id="address" placeholder='Dirección' /> 
            </div> 
            </div>

            <div className='flex md:flex-col md:gap-2 w-full gap-5 text-slate-600'>
            <div className='flex flex-col gap-1 mb-2 w-full'>
                <label htmlFor="phone"> Teléfono </label>
                <input onChange={inputHandle} value={state.phone} type="text" className='w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md' name="phone" id="phone" placeholder='Teléfono' /> 
            </div>

            <div className='flex flex-col gap-1 mb-2 w-full'>
                <label htmlFor="postCode"> Código postal </label>
                <input onChange={inputHandle} value={state.postCode} type="text" className={'w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md'} name="postCode" id="postCode" placeholder='Código postal (opcional)' />
            </div>
            </div>

            <div className='flex md:flex-col md:gap-2 w-full gap-5 text-slate-600'>
            {/* El departamento es una lista y no texto libre: «Guate»,
                «guatemala» y «Guatemala City» son el mismo lugar y ninguna
                comparación los junta, así que la política de envío no podría
                decidir cuánto cuesta ni quién lo paga. */}
            <div className='flex flex-col gap-1 mb-2 w-full'>
                <label htmlFor="department"> Departamento </label>
                <select onChange={inputHandle} value={state.department} className={'w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md'} name="department" id="department">
                    <option value=''>Elegí tu departamento…</option>
                    {(departamentos || []).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                {pol.flete !== null && (
                    <span className='text-xs text-slate-500'>
                        {porCobrar
                            ? 'El envío lo pagás a la mensajería al recibir.'
                            : `Envío a domicilio: Q${fleteEfectivo.toFixed(2)}.`}
                    </span>
                )}
            </div>

            {/* El municipio también es una lista, y los que se ofrecen son los
                del departamento elegido. Como texto libre, «Mixco» y «mixco»
                quedaban como dos destinos distintos. */}
            <div className='flex flex-col gap-1 mb-2 w-full'>
                <label htmlFor="municipality"> Municipio </label>
                <select onChange={inputHandle} value={state.municipality} disabled={!state.department} className={'w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md disabled:bg-slate-50 disabled:text-slate-400'} name="municipality" id="municipality">
                    <option value=''>{state.department ? 'Elegí tu municipio…' : 'Elegí primero el departamento'}</option>
                    {((municipios || {})[state.department] || []).map(m => <option key={m} value={m}>{m}</option>)}
                </select>
            </div>
            </div>


            <div className='flex md:flex-col md:gap-2 w-full gap-5 text-slate-600'>
            <div className='flex flex-col gap-1 mb-2 w-full'>
                <label htmlFor="zona"> Zona o referencias </label>
                <input onChange={inputHandle} value={state.zona} type="text" className={'w-full px-3 py-2 border border-slate-200 outline-none focus:border-green-500 rounded-md'} name="zona" id="zona" placeholder='Zona, colonia o cómo llegar' />
            </div>

            <div className='flex flex-col gap-1 mt-7 mb-2 w-full'>
               <button className='px-3 py-[6px] rounded-sm hover:shadow-green-500/50 hover:shadow-lg bg-green-500 text-white'>Guardar entrega</button>
            </div>
            </div> 
                </form>

                
                </>
            }

            {
                res && <div className='flex flex-col gap-1'>
                <h2 className='text-slate-600 font-semibold pb-2'>Deliver To {state.name}</h2>
                <p>
                    <span className='bg-blue-200 text-blue-800 text-sm font-medium mr-2 px-2 py-1 rounded'>Home</span>
                    <span>{state.phone} · {[state.address, state.zona, state.municipality, state.department].filter(Boolean).join(', ')}</span>

                    <span onClick={() => setRes(false)} className='text-indigo-500 cursor-pointer'>Change </span>
                </p>

                <p className='text-slate-600 text-sm' >Email To ariyan@gmail.com</p>

            </div>
            }
              </div>

              {/* El carrito manda una lista plana; "comprar ahora" manda grupos con
                  productInfo. Se aplanan para pintar lo mismo en los dos casos. */}
              {lineas.map((pt, i) => (
                   <div key={i} className='flex bg-white p-4 flex-col gap-2'>
                       <div className='w-full flex flex-wrap'>
                           <div className='flex sm:w-full gap-2 w-7/12'>
                               <div className='flex gap-2 justify-start items-center'>
                                   <img className='w-[80px] h-[80px] object-cover' src={imgUrl(pt.image)} alt='' />
                                   <div className='pr-4 text-slate-600'>
                                       <h2 className='text-md font-semibold'>{pt.name}</h2>
                                       {pt.brand && <span className='text-sm'>Marca: {pt.brand}</span>}
                                       {pt.bajo_pedido && <p className='text-xs text-amber-700'>Bajo pedido · hasta {pt.dias_entrega} día{pt.dias_entrega === 1 ? '' : 's'} hábil{pt.dias_entrega === 1 ? '' : 'es'}</p>}
                                   </div>
                               </div>
                           </div>
                           <div className='flex justify-between w-5/12 sm:w-full sm:mt-3'>
                               <div className='pl-4 sm:pl-0'>
                                   <h2 className='text-lg text-orange-500'>Q{pt.price} × {pt.quantity}</h2>
                               </div>
                           </div>
                       </div>
                   </div>
              ))}
 

                </div> 
            </div>

            <div className='w-[33%] md-lg:w-full'>
    <div className='pl-3 md-lg:pl-0 md-lg:mt-5'>
        
            <div className='bg-white p-3 text-slate-600 flex flex-col gap-3'>
                <h2 className='text-xl font-bold'>Order Summary</h2>
                <ReservaBanner checkout={checkout} checkoutError={checkoutError} loading={checkoutLoading} onRenew={renovar} onExpired={renovar} />
                <div className='flex justify-between items-center'>
                    <span>Items Total (items) </span>
                    <span>${price}</span>
                </div>
                <div className='flex justify-between items-center'>
                    <span>Envío</span>
                    <span>
                        {pol.flete === null ? <em className='text-slate-400 text-sm'>Elegí tu departamento</em>
                         : porCobrar ? <em className='text-slate-500 text-sm'>Lo pagás al recibir</em>
                         : `Q${fleteEfectivo.toFixed(2)}`}
                    </span>
                </div>

                <div className='flex justify-between items-center font-semibold'>
                    <span>Total</span>
                    <span className='text-lg text-[#059473]'>Q{(price + fleteEfectivo).toFixed(2)}</span>
                </div>
                {porCobrar && (
                    <p className='text-xs text-slate-600 bg-slate-50 rounded-md px-3 py-2'>
                        A {state.department} el envío lo cobra la mensajería cuando te entrega, al
                        precio que ella fije según el peso y el destino. Por eso tu total es solo la
                        mercadería y la compra se paga por adelantado.
                    </p>
                )}
                <BloquePago metodos={metodosPago} elegido={metodo} onElegir={setMetodo}
                            referencia={referencia} onReferencia={setReferencia}
                            total={price + fleteEfectivo} topeCod={codMontoMaximo}
                            porCobrar={porCobrar} departamento={state.department} />
                <button onClick={placeOrder} disabled={!res || !reservaVigente || checkoutLoading} className={`px-5 py-[6px] rounded-sm hover:shadow-red-500/50 hover:shadow-lg ${res && reservaVigente && metodo ? 'bg-red-500' : 'bg-red-300'}  text-sm text-white uppercase`}>
                   Confirmar pedido 
                </button>

            </div>
        

    </div>

</div>



            </div>  

        </div>
        
        
   </section>       

          <Footer/>
        </div>
    );
};

export default Shipping;